import { useCallback, useEffect, useMemo, useState } from 'react'
import type { CSSProperties, FormEvent } from 'react'
import { apiJson, ApiError } from '../api'
import type { ThemeTokens } from '../theme'
import { pageStyles } from '../styles'
import { DataTable } from '../components/DataTable'
import type { DataTableColumn } from '../components/DataTable'
import { BurnBar } from '../components/BurnBar'
import { StatCard } from '../components/StatCard'
import { DEV_KEY_DEFAULT_BUDGET, DEV_KEY_PREFIX } from '../constants'
import { DirectoryPicker, dirEmail } from '../components/DirectoryPicker'
import type { DirectoryUser } from '../components/DirectoryPicker'

/** `/key/list` entries (`GET /api/keys` in `backend/src/routes/keys.ts`).
 *  The Keys page now lists EVERY LiteLLM virtual key, not just developer
 *  keys, so this shape covers the gateway/teams/service keys too. */
interface GatewayKey {
  token?: string
  key_alias?: string
  user_id?: string
  spend?: number
  max_budget?: number | null
  blocked?: boolean
  created_at?: string
  metadata?: { owner?: string; [key: string]: unknown }
  [key: string]: unknown
}

/** Coarse category of a virtual key, derived from its alias, for the Type
 *  column. Heuristic (LiteLLM keys carry no explicit "kind"), matching the
 *  alias conventions in AGENTS.md / terraform: the LibreChat shared gateway
 *  key (`librechat-server-r2-r2`), the Teams bot key, per-dev keys
 *  (`dev-*`), and everything else as a service key. */
type KeyCategory = 'developer' | 'gateway' | 'teams' | 'service'

function keyCategory(key: GatewayKey): KeyCategory {
  const alias = (key.key_alias ?? '').toLowerCase()
  if (alias.startsWith(DEV_KEY_PREFIX)) {
    return 'developer'
  }
  if (alias.includes('librechat') || alias === 'librechat-server-r2-r2') {
    return 'gateway'
  }
  if (alias.includes('teams')) {
    return 'teams'
  }
  return 'service'
}

const CATEGORY_LABEL: Record<KeyCategory, string> = {
  developer: 'Developer',
  gateway: 'Gateway',
  teams: 'Teams',
  service: 'Service',
}

/**
 * What a key's OWNER column is allowed to contain (D6).
 *
 * The old rule was `metadata.owner ?? user_id ?? '-'`, and the `claude-code`
 * key carries no `metadata.owner`, so it fell through to LiteLLM's internal
 * `user_id` GUID. A GUID under a column headed "Owner" reads as a person and
 * is not one — it is the absence of a person, rendered as if it were one.
 *
 * The resolution order is now explicit and terminates in an honest badge
 * rather than a fallback: `metadata.owner`, then `user_id` ONLY if it parses
 * as an email, otherwise UNATTRIBUTED with the raw id kept as a monospace
 * hint so it is still traceable.
 */
export type KeyOwnership =
  | { kind: 'owner'; label: string }
  | { kind: 'unattributed'; rawId: string | null }

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function keyOwnership(key: GatewayKey): KeyOwnership {
  const declared = key.metadata?.owner
  if (typeof declared === 'string' && declared.trim().length > 0) {
    return { kind: 'owner', label: declared.trim() }
  }
  const userId = typeof key.user_id === 'string' ? key.user_id.trim() : ''
  if (EMAIL_RE.test(userId)) {
    return { kind: 'owner', label: userId }
  }
  // Only the GATEWAY and TEAMS keys legitimately belong to the platform
  // rather than to a person: they are named infrastructure, and nobody is
  // supposed to own them.
  //
  // A `service` key is NOT in that set, and the claude-code key is exactly
  // why. Its alias does not start with `dev-`, so treating every
  // non-developer key as "platform" would have quietly declared it owned by
  // nobody-in-particular — which is the same false reassurance the GUID gave,
  // just in nicer words, and it would have hidden the Assign owner action
  // that repairs it. An unowned service key is UNATTRIBUTED, because that is
  // what it is.
  const category = keyCategory(key)
  if (category === 'gateway' || category === 'teams') {
    return { kind: 'owner', label: 'platform' }
  }
  return { kind: 'unattributed', rawId: userId.length > 0 ? userId : null }
}

interface MintDevKeyResult {
  key: string
  alias: string
  budget: number
}

function money(value: number | undefined): string {
  return `$${(value ?? 0).toFixed(2)}`
}

function formatCreated(value: string | undefined): string {
  if (!value) {
    return '-'
  }
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('en-AU', { timeZone: 'Australia/Sydney' })
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    return `${err.message} (HTTP ${err.status})`
  }
  return err instanceof Error ? err.message : String(err)
}

/** A row-scale variant of a page-level button style (D14): the page's own
 *  `buttonGhost`/`buttonDanger` are sized for a toolbar, and three of them
 *  side by side in a table cell is the exact wrapping D7 fixed by hiding
 *  them behind "...". Same colours, smaller padding and type. */
function compactButtonStyle(base: CSSProperties | undefined): CSSProperties {
  return { ...base, padding: '0.3rem 0.65rem', fontSize: '0.75rem' }
}

export function KeysPage({ tokens }: { tokens: ThemeTokens }) {
  const styles = useMemo(() => pageStyles(tokens), [tokens])

  const [keys, setKeys] = useState<GatewayKey[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busyToken, setBusyToken] = useState<string | null>(null)

  const [mintOpen, setMintOpen] = useState(false)
  const [mintEmail, setMintEmail] = useState('')
  const [mintBudget, setMintBudget] = useState(String(DEV_KEY_DEFAULT_BUDGET))
  const [mintError, setMintError] = useState<string | null>(null)
  const [minting, setMinting] = useState(false)
  // The once-shown secret: lives ONLY in this piece of local state, never
  // written to `keys`, localStorage, or anywhere else. Cleared the moment
  // the modal is dismissed.
  const [mintResult, setMintResult] = useState<MintDevKeyResult | null>(null)
  const [copied, setCopied] = useState(false)

  const [deleteTarget, setDeleteTarget] = useState<GatewayKey | null>(null)
  const [deleteText, setDeleteText] = useState('')
  const [deleting, setDeleting] = useState(false)

  // Which row's overflow menu is open. At most one, across the whole table:
  // the panel-wide rule this page now follows is that a table cell holds no
  // more than ONE interactive control, and everything else lives behind this
  // menu or in the detail drawer.
  const [openMenuToken, setOpenMenuToken] = useState<string | null>(null)
  // The row whose budget / owner / detail is being edited in a drawer.
  const [detailTarget, setDetailTarget] = useState<GatewayKey | null>(null)
  const [detailBudget, setDetailBudget] = useState('')
  const [ownerTarget, setOwnerTarget] = useState<GatewayKey | null>(null)
  const [ownerSaving, setOwnerSaving] = useState(false)
  const [ownerError, setOwnerError] = useState<string | null>(null)

  const loadKeys = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await apiJson<{ keys: GatewayKey[] }>('/api/keys')
      // Show EVERY LiteLLM virtual key (gateway, teams, per-dev, service),
      // not just the dev-* aliases the page used to filter to. The Type
      // column below classifies them.
      setKeys(data.keys)
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadKeys()
  }, [loadKeys])

  function openMint() {
    setMintEmail('')
    setMintBudget(String(DEV_KEY_DEFAULT_BUDGET))
    setMintError(null)
    setMintOpen(true)
  }

  async function submitMint(event: FormEvent) {
    event.preventDefault()
    if (!mintEmail.trim()) {
      setMintError('Email is required.')
      return
    }
    const budgetValue = mintBudget.trim() === '' ? undefined : Number(mintBudget)
    if (budgetValue !== undefined && (Number.isNaN(budgetValue) || budgetValue < 0)) {
      setMintError('Budget must be a non-negative number.')
      return
    }

    setMinting(true)
    setMintError(null)
    try {
      const body: Record<string, unknown> = { email: mintEmail.trim() }
      if (budgetValue !== undefined) {
        body.budget = budgetValue
      }
      const result = await apiJson<MintDevKeyResult>('/api/keys/dev', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      setMintOpen(false)
      setMintResult(result)
      setCopied(false)
      await loadKeys()
    } catch (err) {
      setMintError(errorMessage(err))
    } finally {
      setMinting(false)
    }
  }

  function closeSecretModal() {
    // Drop the raw key from state entirely — this is the only place it ever
    // lived on the client.
    setMintResult(null)
    setCopied(false)
  }

  async function copySecret() {
    if (!mintResult) {
      return
    }
    try {
      await navigator.clipboard.writeText(mintResult.key)
      setCopied(true)
    } catch {
      setCopied(false)
    }
  }

  async function toggleBlock(key: GatewayKey) {
    if (!key.token) {
      return
    }
    setBusyToken(key.token)
    setError(null)
    try {
      const action = key.blocked ? 'unblock' : 'block'
      await apiJson(`/api/keys/${encodeURIComponent(key.token)}/${action}`, { method: 'POST' })
      setKeys((prev) => prev.map((k) => (k.token === key.token ? { ...k, blocked: !k.blocked } : k)))
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusyToken(null)
    }
  }

  async function setKeyBudget(token: string, maxBudget: number) {
    setBusyToken(token)
    setError(null)
    try {
      await apiJson(`/api/keys/${encodeURIComponent(token)}/budget`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ maxBudget }),
      })
      setKeys((prev) => prev.map((k) => (k.token === token ? { ...k, max_budget: maxBudget } : k)))
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusyToken(null)
    }
  }

  function openDelete(key: GatewayKey) {
    setOpenMenuToken(null)
    setDeleteTarget(key)
    setDeleteText('')
  }

  function openDetail(key: GatewayKey) {
    setOpenMenuToken(null)
    setDetailTarget(key)
    setDetailBudget(key.max_budget != null ? String(key.max_budget) : '')
  }

  function openAssignOwner(key: GatewayKey) {
    setOpenMenuToken(null)
    setOwnerError(null)
    setOwnerTarget(key)
  }

  /**
   * The repair half of D6: stamp a real person onto an unattributed key.
   *
   * This is what makes the orphan check exact rather than a string match on
   * a metadata blob — after this the `claude-code` key says a name, and says
   * it permanently, because it is written back to the gateway rather than
   * decorated in the browser.
   */
  async function assignOwner(user: DirectoryUser) {
    const key = ownerTarget
    const email = dirEmail(user)
    if (!key?.token || !email) {
      return
    }
    setOwnerSaving(true)
    setOwnerError(null)
    try {
      await apiJson(`/api/keys/${encodeURIComponent(key.token)}/owner`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, entraOid: user.id }),
      })
      setOwnerTarget(null)
      await loadKeys()
    } catch (err) {
      setOwnerError(errorMessage(err))
    } finally {
      setOwnerSaving(false)
    }
  }

  async function confirmDelete() {
    if (!deleteTarget?.token || deleteText !== 'DELETE') {
      return
    }
    setDeleting(true)
    setError(null)
    try {
      await apiJson(`/api/keys/${encodeURIComponent(deleteTarget.token)}`, { method: 'DELETE' })
      setKeys((prev) => prev.filter((k) => k.token !== deleteTarget.token))
      setDeleteTarget(null)
      setDeleteText('')
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setDeleting(false)
    }
  }

  const blockedCount = keys.filter((k) => k.blocked).length
  const devCount = keys.filter((k) => keyCategory(k) === 'developer').length
  const totalSpend = keys.reduce((sum, k) => sum + (k.spend ?? 0), 0)
  const unattributedCount = keys.filter((k) => keyOwnership(k).kind === 'unattributed').length

  /**
   * UNATTRIBUTED sorts to the top, always. A key with no owner is the whole
   * reason this page has an Owner column — it is the key whose person may
   * have left — so it is never something you have to go looking for.
   */
  const sortedKeys = useMemo(() => {
    const rank = (k: GatewayKey) => (keyOwnership(k).kind === 'unattributed' ? 0 : 1)
    return [...keys].sort(
      (a, b) => rank(a) - rank(b) || (a.key_alias ?? '').localeCompare(b.key_alias ?? ''),
    )
  }, [keys])

  const categoryBadge = (cat: KeyCategory) => {
    const bg = cat === 'developer' ? tokens.brand : tokens.surfaceRaised
    const color = cat === 'developer' ? tokens.brandContrast : tokens.textSecondary
    return (
      <span style={{ ...styles.badge, background: bg, color, border: `1px solid ${tokens.border}` }}>
        {CATEGORY_LABEL[cat]}
      </span>
    )
  }

  /**
   * The panel-wide layout rule this page now follows:
   *
   *   No table cell contains more than one interactive control.
   *
   * The Actions cell used to be a `flexWrap: 'wrap'` row holding three
   * buttons and a number input, inside a table that also had to fit alias,
   * type, owner, spend, a burn bar, status and created. It wrapped because
   * there was more control in that cell than there was column. The wrapping
   * was the symptom; the rule is the fix. Everything but the overflow
   * trigger has moved into the row's detail drawer.
   */
  const columns: Array<DataTableColumn<GatewayKey>> = [
    {
      key: 'alias',
      header: 'Alias',
      render: (k) => (
        <span style={{ fontWeight: 600 }}>{k.key_alias ?? (k.token ? `${k.token.slice(0, 12)}…` : '?')}</span>
      ),
    },
    { key: 'type', header: 'Type', render: (k) => categoryBadge(keyCategory(k)) },
    {
      key: 'owner',
      header: 'Owner',
      render: (k) => {
        const ownership = keyOwnership(k)
        if (ownership.kind === 'owner') {
          // A link only when the label is actually email-shaped: 'platform'
          // and a free-text automation name (n8n-dev) are real answers to
          // "who owns this key" that are not a row on the People page, and
          // a dead link there would be worse than none.
          return EMAIL_RE.test(ownership.label) ? (
            <a href={`/people?person=${encodeURIComponent(ownership.label)}`} style={{ color: tokens.textPrimary }}>
              {ownership.label}
            </a>
          ) : (
            <span>{ownership.label}</span>
          )
        }
        // Never a GUID under a column headed Owner. The badge says what is
        // actually true — nobody is attached to this key — and the raw id
        // stays visible as a hint so the key is still traceable.
        return (
          <span style={{ display: 'inline-flex', flexDirection: 'column', gap: '0.15rem' }}>
            <span style={{ ...styles.badge, background: tokens.danger, color: tokens.dangerContrast }}>
              Unattributed
            </span>
            {ownership.rawId ? (
              <span
                style={{
                  ...styles.hint,
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  fontSize: '0.7rem',
                }}
                title={ownership.rawId}
              >
                {ownership.rawId.length > 14 ? `${ownership.rawId.slice(0, 14)}…` : ownership.rawId}
              </span>
            ) : null}
          </span>
        )
      },
    },
    {
      key: 'spend',
      header: 'Spend / budget',
      align: 'right',
      render: (k) => (
        <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.2rem' }}>
          <span>
            {money(k.spend)}
            {k.max_budget != null ? ` / ${money(k.max_budget)}` : ''}
          </span>
          <BurnBar spend={k.spend ?? 0} cap={k.max_budget ?? null} tokens={tokens} />
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (k) =>
        k.blocked ? (
          <span style={{ ...styles.badge, background: tokens.danger, color: tokens.dangerContrast }}>Blocked</span>
        ) : (
          <span style={{ ...styles.badge, background: tokens.brand, color: tokens.brandContrast }}>Active</span>
        ),
    },
    { key: 'created', header: 'Created', render: (k) => formatCreated(k.created_at) },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: '15rem',
      render: (k) => {
        if (!k.token) {
          return <span style={styles.hint}>no key token</span>
        }
        const token = k.token
        const open = openMenuToken === token
        const isDev = keyCategory(k) === 'developer'
        return (
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', whiteSpace: 'nowrap' }}>
            {/* D14: Block and Delete moved back out from behind the overflow
                menu -- D7 collapsed every row action into "..." to fix a
                cell that wrapped with three buttons and a number input, but
                that also hid the two actions people actually reach for, and
                Maroun could not find them. Only these two come back as
                visible buttons; Details/Set budget (the drawer) and Assign
                owner stay behind "..." because they are looked-up-rarely,
                not day-to-day, and reintroducing all of them is the same
                wrap this row already failed once. Destructive/blocking
                actions stay scoped to developer keys, same as before:
                platform keys (gateway/Teams/service) are read-only here. */}
            {isDev ? (
              <button
                type="button"
                style={{ ...compactButtonStyle(styles.buttonGhost), opacity: busyToken === token ? 0.6 : 1 }}
                disabled={busyToken === token}
                onClick={() => void toggleBlock(k)}
              >
                {k.blocked ? 'Unblock' : 'Block'}
              </button>
            ) : null}
            {isDev ? (
              <button
                type="button"
                style={{ ...compactButtonStyle(styles.buttonGhost), color: tokens.danger }}
                onClick={() => openDelete(k)}
              >
                Delete
              </button>
            ) : null}
            <div style={styles.pickerWrap}>
              <button
                type="button"
                aria-label={`Actions for ${k.key_alias ?? token}`}
                aria-haspopup="menu"
                aria-expanded={open}
                style={compactButtonStyle(styles.buttonGhost)}
                onClick={() => setOpenMenuToken(open ? null : token)}
              >
                ⋯
              </button>
              {open ? (
                <div style={{ ...styles.pickerDropdown, left: 'auto', right: 0, minWidth: '12rem' }} role="menu">
                  <button type="button" role="menuitem" style={styles.pickerOption} onClick={() => openDetail(k)}>
                    Details
                  </button>
                  {keyOwnership(k).kind === 'unattributed' ? (
                    <button
                      type="button"
                      role="menuitem"
                      style={styles.pickerOption}
                      onClick={() => openAssignOwner(k)}
                    >
                      Assign owner…
                    </button>
                  ) : null}
                  {isDev ? (
                    <button type="button" role="menuitem" style={styles.pickerOption} onClick={() => openDetail(k)}>
                      Set budget…
                    </button>
                  ) : (
                    <div style={{ ...styles.pickerOptionMeta, padding: '0.45rem 0.6rem' }}>
                      Platform key — manage on Budgets
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        )
      },
    },
  ]

  return (
    <div style={{ ...styles.page, maxWidth: 'none' }}>
      <div style={styles.statsRow}>
        <StatCard label="Virtual keys" value={keys.length} tokens={tokens} />
        <StatCard label="Developer keys" value={devCount} tokens={tokens} />
        <StatCard
          label="Unattributed"
          value={unattributedCount}
          hint="no person attached"
          tokens={tokens}
          tone={unattributedCount > 0 ? 'danger' : 'default'}
        />
        <StatCard label="Blocked" value={blockedCount} tokens={tokens} tone={blockedCount > 0 ? 'danger' : 'default'} />
        <StatCard label="Total key spend" value={money(totalSpend)} tokens={tokens} />
      </div>

      <section style={styles.section}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>Virtual keys</h2>
          <div style={{ display: 'flex', gap: '0.6rem' }}>
            <button type="button" style={styles.buttonGhost} onClick={() => void loadKeys()} disabled={loading}>
              {loading ? 'Refreshing…' : 'Refresh'}
            </button>
            <button type="button" style={styles.button} onClick={openMint}>
              Mint developer key
            </button>
          </div>
        </div>
        <p style={styles.sectionSubtitle}>
          Every LiteLLM virtual key on the gateway: the LibreChat shared gateway key, the Teams bot key, per-developer
          keys, and any service keys — classified in the Type column. Developer keys (AU-resident models only, $
          {DEV_KEY_DEFAULT_BUDGET}/30d default) can be blocked or deleted directly from each row (re-budget and
          ownership stay behind the ⋯ menu); platform keys are read-only and their ceilings are managed on Budgets. Raw key secrets are shown exactly once at mint time
          and are never retrievable afterwards. Source: LiteLLM <code>/key/list</code>, read now.
        </p>
        {unattributedCount > 0 ? (
          <p style={{ ...styles.error, fontWeight: 700 }}>
            {unattributedCount} key{unattributedCount === 1 ? '' : 's'} with no person attached, sorted to the top. A
            key outlives the person who asked for it — use <strong>Assign owner</strong> so this stays answerable.
          </p>
        ) : null}
        {error ? <p style={styles.error}>{error}</p> : null}
        {loading ? (
          <p style={styles.hint}>Loading developer keys…</p>
        ) : (
          <DataTable
            columns={columns}
            rows={sortedKeys}
            getRowKey={(k) => k.token ?? k.key_alias ?? 'unknown-key'}
            getRowLabel={(k) => k.key_alias ?? k.token ?? 'key'}
            tokens={tokens}
            emptyLabel="No developer keys minted yet."
          />
        )}
      </section>

      {mintOpen ? (
        <div style={styles.modalOverlay} role="presentation" onClick={() => !minting && setMintOpen(false)}>
          <div style={styles.modalCard} role="dialog" aria-modal="true" aria-label="Mint developer key" onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>Mint developer key</h3>
            <p style={styles.modalBody}>
              Creates an AU-resident-only LiteLLM key for one developer. The raw key is returned once by the
              gateway and shown once here — copy it immediately, it cannot be retrieved again.
            </p>
            <form onSubmit={(e) => void submitMint(e)}>
              <div style={styles.field}>
                <label style={styles.fieldLabel} htmlFor="mint-email">
                  Email
                </label>
                <input
                  id="mint-email"
                  style={styles.input}
                  type="email"
                  value={mintEmail}
                  onChange={(e) => setMintEmail(e.target.value)}
                  placeholder="dave@chu.com.au"
                  disabled={minting}
                />
              </div>
              <div style={styles.field}>
                <label style={styles.fieldLabel} htmlFor="mint-budget">
                  Budget ($/30d, optional — defaults to ${DEV_KEY_DEFAULT_BUDGET})
                </label>
                <input
                  id="mint-budget"
                  style={styles.input}
                  type="number"
                  min={0}
                  value={mintBudget}
                  onChange={(e) => setMintBudget(e.target.value)}
                  disabled={minting}
                />
              </div>
              {mintError ? <p style={styles.error}>{mintError}</p> : null}
              <div style={styles.modalActions}>
                <button type="button" style={styles.buttonGhost} onClick={() => setMintOpen(false)} disabled={minting}>
                  Cancel
                </button>
                <button type="submit" style={styles.button} disabled={minting}>
                  {minting ? 'Minting…' : 'Mint'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {mintResult ? (
        <div style={styles.modalOverlay} role="presentation">
          <div style={styles.modalCard} role="dialog" aria-modal="true" aria-label="Developer key created">
            <h3 style={styles.modalTitle}>Key minted: {mintResult.alias}</h3>
            <p style={styles.warningCallout}>
              This key is shown once and cannot be retrieved again. Copy it now — it is not stored anywhere in this
              app after you close this dialog.
            </p>
            <div style={{ ...styles.field, marginTop: '0.9rem' }}>
              <span style={styles.fieldLabel}>Budget: ${mintResult.budget}/30d</span>
              <div style={styles.secretBox}>{mintResult.key}</div>
            </div>
            <div style={styles.modalActions}>
              <button type="button" style={styles.buttonGhost} onClick={() => void copySecret()}>
                {copied ? 'Copied' : 'Copy'}
              </button>
              <button type="button" style={styles.button} onClick={closeSecretModal}>
                Done
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* The row detail drawer. Everything that used to be crammed into the
          Actions cell lives here: the budget input and its submit, plus the
          key's full facts. One control per cell, up there; as many as the
          job needs, in here. */}
      {detailTarget ? (
        <div style={styles.modalOverlay} role="presentation" onClick={() => setDetailTarget(null)}>
          <div
            style={styles.modalCard}
            role="dialog"
            aria-modal="true"
            aria-label={`Key ${detailTarget.key_alias ?? detailTarget.token}`}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={styles.modalTitle}>{detailTarget.key_alias ?? detailTarget.token}</h3>
            <p style={styles.modalBody}>
              {CATEGORY_LABEL[keyCategory(detailTarget)]} key · created {formatCreated(detailTarget.created_at)}
            </p>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '8rem minmax(0,1fr)',
                gap: '0.35rem 0.9rem',
                fontSize: '0.85rem',
                marginBottom: '1rem',
              }}
            >
              <span style={styles.fieldLabel}>Owner</span>
              <span style={{ color: tokens.textPrimary }}>
                {keyOwnership(detailTarget).kind === 'owner'
                  ? (keyOwnership(detailTarget) as { label: string }).label
                  : 'UNATTRIBUTED'}
              </span>
              <span style={styles.fieldLabel}>Spend</span>
              <span style={{ color: tokens.textPrimary }}>{money(detailTarget.spend)}</span>
              <span style={styles.fieldLabel}>Budget</span>
              <span style={{ color: tokens.textPrimary }}>
                {detailTarget.max_budget != null ? money(detailTarget.max_budget) : 'none'}
              </span>
              <span style={styles.fieldLabel}>Status</span>
              <span style={{ color: detailTarget.blocked ? tokens.danger : tokens.textPrimary }}>
                {detailTarget.blocked ? 'Blocked' : 'Active'}
              </span>
            </div>

            {keyCategory(detailTarget) === 'developer' ? (
              <div style={styles.field}>
                <label style={styles.fieldLabel} htmlFor="detail-budget">
                  Budget ($ / 30d)
                </label>
                <input
                  id="detail-budget"
                  style={styles.input}
                  type="number"
                  min={0}
                  step="1"
                  value={detailBudget}
                  onChange={(e) => setDetailBudget(e.target.value)}
                  disabled={busyToken === detailTarget.token}
                />
              </div>
            ) : (
              <p style={styles.hint}>
                Platform key. Its ceiling is managed on the Budgets page; it is deliberately not editable here.
              </p>
            )}

            <div style={styles.modalActions}>
              <button type="button" style={styles.buttonGhost} onClick={() => setDetailTarget(null)}>
                Close
              </button>
              {keyCategory(detailTarget) === 'developer' ? (
                <button
                  type="button"
                  style={styles.button}
                  disabled={
                    busyToken === detailTarget.token ||
                    detailBudget.trim() === '' ||
                    Number.isNaN(Number(detailBudget)) ||
                    Number(detailBudget) < 0
                  }
                  onClick={() => {
                    const token = detailTarget.token
                    if (token) {
                      void setKeyBudget(token, Number(detailBudget)).then(() => setDetailTarget(null))
                    }
                  }}
                >
                  Save budget
                </button>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      {ownerTarget ? (
        <div style={styles.modalOverlay} role="presentation" onClick={() => !ownerSaving && setOwnerTarget(null)}>
          <div
            style={styles.modalCard}
            role="dialog"
            aria-modal="true"
            aria-label="Assign key owner"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={styles.modalTitle}>Assign owner — {ownerTarget.key_alias ?? ownerTarget.token}</h3>
            <p style={styles.modalBody}>
              This key has no person attached, so the panel cannot tell you whose it is or whether they have left.
              Picking someone writes <code>metadata.owner</code>, <code>metadata.entra_oid</code> and{' '}
              <code>user_id</code> back to the gateway permanently, and the change is recorded in the audit log.
            </p>
            <DirectoryPicker
              tokens={tokens}
              disabled={ownerSaving}
              onPick={(u) => void assignOwner(u)}
              placeholder="Search the directory for the key's owner…"
            />
            {ownerError ? <p style={styles.error}>{ownerError}</p> : null}
            <div style={styles.modalActions}>
              <button
                type="button"
                style={styles.buttonGhost}
                onClick={() => setOwnerTarget(null)}
                disabled={ownerSaving}
              >
                {ownerSaving ? 'Saving…' : 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {deleteTarget ? (
        <div style={styles.modalOverlay} role="presentation" onClick={() => !deleting && setDeleteTarget(null)}>
          <div
            style={styles.modalCard}
            role="dialog"
            aria-modal="true"
            aria-label="Delete developer key"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={styles.modalTitle}>Delete {deleteTarget.key_alias ?? deleteTarget.token}?</h3>
            <p style={styles.modalBody}>
              This permanently deletes the key. Anyone using it loses gateway access immediately. This cannot be
              undone.
            </p>
            <div style={styles.field}>
              <label style={styles.fieldLabel} htmlFor="delete-confirm-text">
                Type DELETE to confirm
              </label>
              <input
                id="delete-confirm-text"
                style={styles.input}
                value={deleteText}
                onChange={(e) => setDeleteText(e.target.value)}
                disabled={deleting}
                autoComplete="off"
              />
            </div>
            <div style={styles.modalActions}>
              <button type="button" style={styles.buttonGhost} onClick={() => setDeleteTarget(null)} disabled={deleting}>
                Cancel
              </button>
              <button type="button" style={styles.buttonDanger} disabled={deleteText !== 'DELETE' || deleting} onClick={() => void confirmDelete()}>
                {deleting ? 'Deleting…' : 'Permanently delete'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

export default KeysPage
